import React from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { LegalPageLayout, Section, Callout, Bullets } from '@/components/LegalPageLayout';

const UPDATED = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const RefundPolicy = () => (
  <LegalPageLayout
    title="Refund Policy"
    lastUpdated={UPDATED}
    icon={RefreshCw}
    testid="refund-page"
    intro={
      <p>
        This policy describes how subscriptions, trials, renewals, cancellations and refunds work for the service
        operated by <strong>Option Wizard</strong>, based on the platform&rsquo;s
        current billing configuration. See also our{' '}
        <Link to="/terms" className="text-amber-300 hover:underline underline-offset-4">Terms of Service</Link>.
      </p>
    }
  >
    <Section title="1. Subscription Plans (verified from implementation)">
      <Bullets items={[
        'Monthly plan: $19.99 per month (USD).',
        'Annual plan: $99.99 per year (USD).',
        'Free trial: a 1-day free trial is offered to eligible new subscribers (one trial per account).',
        'Automatic renewal: subscriptions renew automatically at the end of each billing period unless cancelled beforehand.',
        'Billing is processed by our payment processor, Stripe.',
      ]} />
    </Section>

    <Section title="2. Cancellation (verified from implementation)">
      <p>
        You can cancel at any time from the Stripe billing portal (accessible from your account). When you cancel, your
        subscription remains active until the end of the current billing period and then does not renew. Cancelling
        during the free trial before it ends prevents the first charge.
      </p>
    </Section>

    <Section title="3. Refund Policy">
      <Callout tone="amber" title="Scope">
        No money-back guarantee beyond the terms below is promised, and no blanket no-refund rule applies.
      </Callout>
      <Bullets items={[
        'Billing errors: duplicate charges and verified billing errors are eligible for a refund.',
        'Voluntary 7-day window: you may request a voluntary refund within seven (7) days following your initial paid subscription charge (the first charge after any free trial).',
        'Recurring charges: the voluntary 7-day window applies only to the initial paid subscription charge. It does not automatically extend to subsequent recurring renewal charges.',
        'Cancellation: you may cancel at any time through the billing portal to prevent future renewals. Cancellation alone does not automatically guarantee a refund of amounts already charged.',
        'Applicable law: nothing in this policy limits your rights to refunds or other remedies required by the laws that apply to you.',
      ]} />
    </Section>

    <Section title="4. Duplicate Charges & Billing Errors">
      <p>
        If you believe you were charged in error or more than once, contact us promptly with the details. Verified
        duplicate charges and genuine billing errors will be addressed.
      </p>
    </Section>

    <Section title="5. Failed Payments & Account Access">
      <p>
        If a renewal payment fails, access to premium features may be suspended until payment is resolved, consistent
        with the subscription status reported by Stripe.
      </p>
    </Section>

    <Section title="6. How to Request a Refund or Raise a Billing Issue">
      <p>
        Submit a request through our{' '}
        <Link to="/contact" className="text-amber-300 hover:underline underline-offset-4">contact form</Link>, including
        your account email, the charge in question (date and amount), and a short description of the issue (e.g.,
        duplicate charge, billing error, or a request within the voluntary 7-day window). A specific response or
        processing time has not been established and is not guaranteed.
      </p>
    </Section>

    <Section title="7. Statutory Rights">
      <p>
        Nothing in this policy limits any non-waivable refund or cancellation rights you may have under the consumer-
        protection laws that apply to you.
      </p>
    </Section>
  </LegalPageLayout>
);

export default RefundPolicy;
